import { DataClassBase } from "imaginarity-azure";
import * as React from 'react';
import { createDispatchHook, createSelectorHook, createStoreHook, Provider, ReactReduxContextValue } from 'react-redux';
import { createSubscription } from "react-redux/es/utils/Subscription";
import { AnyAction, createStore, Dispatch, Store } from 'redux';
import { AppRouteNames, ViewRouteNames } from "services/AppEntryDefinitions";
import { useImgI18N } from "services/ImgI18N";
import { GeneralPostEditAction, generalPostEditReducer, GeneralPostEditState } from './GeneralPostEditStateHandling';
import PostUpdater from './PostUpdater';
import CategoryEdit from "./TypeImplementations/CategoryEdit";
import PostEdit from './TypeImplementations/PostEdit';

export interface BaseEditItem extends DataClassBase {
    group_id: string;
}

interface GeneralPostEditProps<T> {
    post?: T;
    onSave?: (post: T) => void;
    onCancel?: () => void;
    onChangeNeedSave?: (value: boolean) => void;
    onMount?: (save: () => void, cancel: () => void) => void;
    onDismount?: () => void;
    groupId?: string;
    apiRoute?: string;
    path?: Array<ViewRouteNames | AppRouteNames>;
}
export type GeneralPostEditorProps<T> = Omit<GeneralPostEditProps<T>, "post" | "groupId">;
export const generalPostEditStore = createStore(generalPostEditReducer);
export const GeneralPostEditContext = React.createContext<ReactReduxContextValue<any, AnyAction>>({
    store: generalPostEditStore,
    subscription: createSubscription(generalPostEditStore)
});

type UseSelectorGPEType = <TSelected = unknown>(selector: (state: GeneralPostEditState) => TSelected, equalityFn?: ((left: TSelected, right: TSelected) => boolean) | undefined) => TSelected
type UseDispatchGPEType = () => Dispatch<GeneralPostEditAction>;
type UseStoreGPEType = () => Store<GeneralPostEditState, GeneralPostEditAction>
export const useStoreGPE = createStoreHook(GeneralPostEditContext) as UseStoreGPEType;
export const useDispatchGPE = createDispatchHook(GeneralPostEditContext) as UseDispatchGPEType;
export const useSelectorGPE = createSelectorHook(GeneralPostEditContext) as UseSelectorGPEType;

const knownPostTypes = ["PorscheMomentPost", "ContentPost", "TubePost", "Quiz", "MultipleChoiceQuestion", "LearnerCertificate",
    'EstimateQuestion', 'MarkInPictureQuestion', 'MarkAreaInPictureQuestion', 'SortQuestion', 'CorrelatePairsQuestion', 'FourPicturesOneSolutionQuestion',
]
export const knownCatTypes = ["Category", "ContentPostContentType", "ContentPostContentLocation", "ContentPostMediaType", "Competence"];

const GeneralPostEdit = <T extends BaseEditItem = BaseEditItem>(p: GeneralPostEditProps<T> & { children?: JSX.Element | JSX.Element[] }) => {
    const { post, onSave, onCancel, onChangeNeedSave, onMount, onDismount, groupId, apiRoute, children, path } = p;
    const type = post?.type ?? "";
    const { t } = useImgI18N("admin");

    const Content = React.useMemo(() => () => {
        if (knownPostTypes.indexOf(type) >= 0)
            return <PostEdit onSave={onSave as any} onCancel={onCancel} onChangeNeedSave={onChangeNeedSave} onMount={onMount} onDismount={onDismount} apiRoute={apiRoute} />;
        if (knownCatTypes.indexOf(type) >= 0)
            return <CategoryEdit onSave={onSave as any} onCancel={onCancel} onChangeNeedSave={onChangeNeedSave} onMount={onMount} onDismount={onDismount} apiRoute={apiRoute} path={path} />;
        //return <div >It's a category!</div>;
        return <div >{t("unknown post type!")}</div>;
    }, [type, onSave, onCancel, onChangeNeedSave, onMount, onDismount, apiRoute, path, t]);

    return (
        <Provider context={GeneralPostEditContext} store={generalPostEditStore} >
            <PostUpdater post={p.post} groupId={groupId} isPost={knownPostTypes.indexOf(type) >= 0}>
                {children ? children : <Content />}
            </PostUpdater>
        </Provider >
    );
}
export default GeneralPostEdit;